import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

const SCROLL_DEBOUNCE_MS = 120;
const DEFAULT_AUTOPLAY_SECONDS = 6;
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Hero gallery with an opacity-switched desktop stage and a scroll-snap mobile track.
 *
 * @typedef {Object} HeroGalleryRefs
 * @property {HTMLElement} [scroller]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement} [current]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */
export class UlifHeroGallery extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #scrollerAbortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {HTMLElement | undefined} */
  #boundScroller;

  /** @type {HTMLElement | undefined} */
  #resizeObservedScroller;

  /** @type {HTMLElement | undefined} */
  #leadingSentinel;

  /** @type {HTMLElement | undefined} */
  #trailingSentinel;

  /** @type {WeakSet<HTMLButtonElement>} */
  #boundTabs = new WeakSet();

  /** @type {number | undefined} */
  #scrollTimer;

  /** @type {number | undefined} */
  #programmaticScrollTimer;

  /** @type {number | undefined} */
  #autoplayTimer;

  /** @type {number | undefined} */
  #resizeFrame;

  /** @type {number | undefined} */
  #scrollerInlineSize;

  #currentIndex = 0;
  #programmaticScroll = false;
  #pointerActive = false;
  #resizeAlignmentPending = false;
  #autoplayBlocked = false;
  #legacyMediaQueryListener = false;
  #legacyReducedMotionListener = false;
  #reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#boundTabs = new WeakSet();
    this.#autoplayBlocked = false;

    this.#bindLifecycleEvents();
    this.#bindScrollerEvents();
    this.#bindResizeObserver();
    this.#bindTabs();

    const slides = this.#getSlides();
    if (slides.length === 0) {
      this.dataset.panelCount = '0';
      this.#removeLoopSentinels();
      this.#stopAutoplay();
      return;
    }

    this.dataset.panelCount = `${slides.length}`;
    this.#currentIndex = this.#getInitialIndex(slides.length);
    this.#applyState(this.#currentIndex, {
      animate: false,
      dispatch: false,
      scroll: false,
      trigger: 'select',
    });
    this.#syncMode({ scroll: true });
    this.#startAutoplayIfAllowed();
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.isConnected || !this.#abortController) return;

    queueMicrotask(() => {
      if (!this.isConnected) return;

      const slides = this.#getSlides();
      this.dataset.panelCount = `${slides.length}`;
      this.#bindScrollerEvents();
      this.#bindResizeObserver();
      this.#bindTabs();

      if (slides.length === 0) {
        this.#removeLoopSentinels();
        this.#stopAutoplay();
        return;
      }

      this.#currentIndex = this.#clampIndex(this.#currentIndex, slides.length);
      this.#syncLoopSentinels(slides);
      this.#applyState(this.#currentIndex, {
        animate: false,
        dispatch: false,
        scroll: false,
        trigger: 'select',
      });

      if (this.#isMobile()) {
        this.#scrollToSlide(slides[this.#currentIndex], false, { allowLoopPath: false });
      }

      this.#startAutoplayIfAllowed();
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#scrollerAbortController?.abort();
    this.#scrollerAbortController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#resizeObservedScroller = undefined;
    this.#removeLoopSentinels();
    this.#boundScroller = undefined;

    if (this.#legacyMediaQueryListener) {
      mediaQueryLarge.removeListener(this.#handleBreakpointChange);
      this.#legacyMediaQueryListener = false;
    }
    if (this.#legacyReducedMotionListener) {
      this.#reducedMotionMedia.removeListener(this.#handleReducedMotionChange);
      this.#legacyReducedMotionListener = false;
    }

    this.#clearScrollTimers();
    if (this.#resizeFrame) window.cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = undefined;
    this.#resizeAlignmentPending = false;
    this.#scrollerInlineSize = undefined;
    this.#stopAutoplay();
    super.disconnectedCallback();
  }

  get current() {
    return this.#currentIndex;
  }

  /**
   * Select a panel. Declarative actions call this as select(index, event).
   *
   * @param {number|string|Event} input
   * @param {Event|Object} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate]
   * @param {boolean} [options.dispatch]
   * @param {boolean} [options.focus]
   * @param {boolean} [options.scroll]
   * @param {string} [options.trigger]
   */
  select(input, event, options = {}) {
    const normalized = this.#normalizeSelectArguments(input, event, options);
    const slides = this.#getSlides();
    if (slides.length === 0) return;

    const index = this.#clampIndex(normalized.index, slides.length);
    const interaction = normalized.event;
    const trigger = normalized.options.trigger || this.#triggerFromEvent(interaction);

    if (interaction?.type === 'pointerenter' && (!mediaQueryLarge.matches || !this.#canHover())) return;

    if (this.#isMobile() && interaction && trigger !== 'autoplay' && trigger !== 'editor') {
      this.#pauseForInteraction();
    }

    this.#applyState(index, {
      animate: normalized.options.animate,
      dispatch: normalized.options.dispatch,
      event: interaction,
      focus: normalized.options.focus,
      scroll: normalized.options.scroll,
      trigger,
    });
  }

  /** @param {Event} [event] */
  next(event) {
    const slides = this.#getSlides();
    if (slides.length <= 1) return;

    const index = this.#loopEnabled()
      ? (this.#currentIndex + 1) % slides.length
      : Math.min(this.#currentIndex + 1, slides.length - 1);

    this.select(index, event, { trigger: event ? 'arrow' : 'autoplay' });
  }

  /** @param {Event} [event] */
  previous(event) {
    const slides = this.#getSlides();
    if (slides.length <= 1) return;

    const index = this.#loopEnabled()
      ? (this.#currentIndex - 1 + slides.length) % slides.length
      : Math.max(this.#currentIndex - 1, 0);

    this.select(index, event, { trigger: 'arrow' });
  }

  /** @param {Event} [event] */
  play(event) {
    event?.preventDefault?.();
    this.#autoplayBlocked = false;
    this.#startAutoplayIfAllowed();
  }

  /** @param {Event} [event] */
  pause(event) {
    event?.preventDefault?.();
    this.#autoplayBlocked = true;
    this.#stopAutoplay();
  }

  #bindLifecycleEvents() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    if (typeof mediaQueryLarge.addEventListener === 'function') {
      mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });
    } else {
      mediaQueryLarge.addListener(this.#handleBreakpointChange);
      this.#legacyMediaQueryListener = true;
    }

    if (typeof this.#reducedMotionMedia.addEventListener === 'function') {
      this.#reducedMotionMedia.addEventListener('change', this.#handleReducedMotionChange, { signal });
    } else {
      this.#reducedMotionMedia.addListener(this.#handleReducedMotionChange);
      this.#legacyReducedMotionListener = true;
    }
  }

  #bindScrollerEvents() {
    const scroller = this.#getScroller();
    if (scroller === this.#boundScroller) return;

    this.#scrollerAbortController?.abort();
    this.#scrollerAbortController = undefined;
    this.#boundScroller = scroller;

    if (!scroller) return;

    this.#scrollerAbortController = new AbortController();
    const signal = this.#scrollerAbortController.signal;

    scroller.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    scroller.addEventListener('pointerdown', this.#handlePointerDown, { passive: true, signal });
    scroller.addEventListener('pointerup', this.#handlePointerUp, { passive: true, signal });
    scroller.addEventListener('pointercancel', this.#handlePointerUp, { passive: true, signal });

    if ('onscrollend' in scroller) {
      scroller.addEventListener('scrollend', this.#handleScrollEnd, { signal });
    }
  }

  #bindResizeObserver() {
    const scroller = this.#getScroller();
    if (scroller === this.#resizeObservedScroller) return;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#resizeObservedScroller = scroller;
    this.#scrollerInlineSize = scroller?.getBoundingClientRect().width;

    if (!scroller || typeof window.ResizeObserver !== 'function') return;

    this.#resizeObserver = new ResizeObserver(this.#handleScrollerResize);
    this.#resizeObserver.observe(scroller);
  }

  #bindTabs() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    for (const tab of this.#getTabs()) {
      if (this.#boundTabs.has(tab)) continue;
      this.#boundTabs.add(tab);

      if (!tab.hasAttribute('on:pointerenter')) {
        tab.addEventListener('pointerenter', this.#handleTabPointerEnter, { signal });
      }
      if (!tab.hasAttribute('on:click')) {
        tab.addEventListener('click', this.#handleTabClick, { signal });
      }
      if (!tab.hasAttribute('on:focus')) {
        tab.addEventListener('focus', this.#handleTabFocus, { signal });
      }

      tab.addEventListener('keydown', this.#handleTabKeydown, { signal });
    }
  }

  #handleTabPointerEnter = (event) => {
    if (!mediaQueryLarge.matches || !this.#canHover()) return;
    const index = this.#indexForTab(event.currentTarget);
    if (index !== -1) this.select(index, event, { trigger: 'hover' });
  };

  #handleTabClick = (event) => {
    const index = this.#indexForTab(event.currentTarget);
    if (index !== -1) this.select(index, event, { trigger: 'click' });
  };

  #handleTabFocus = (event) => {
    const index = this.#indexForTab(event.currentTarget);
    if (index !== -1) this.select(index, event, { trigger: 'focus' });
  };

  #handleTabKeydown = (event) => {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;

    const tabs = this.#getTabs();
    const currentIndex = tabs.indexOf(event.currentTarget);
    if (currentIndex === -1 || tabs.length === 0) return;

    let targetIndex;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        targetIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        targetIndex = currentIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(targetIndex, event, { focus: true, trigger: 'keyboard' });
  };

  #handleScroll = () => {
    if (!this.#isMobile()) return;

    if (!this.#programmaticScroll) this.#pauseForInteraction();
    this.#scheduleScrollSync();
  };

  #handleScrollEnd = () => {
    if (!this.#isMobile()) return;

    if (this.#scrollTimer) window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = undefined;
    this.#finishScrollSync();
  };

  #handlePointerDown = () => {
    if (!this.#isMobile()) return;
    this.#pointerActive = true;
    this.#programmaticScroll = false;
    this.#pauseForInteraction();
  };

  #handlePointerUp = () => {
    if (!this.#isMobile()) return;
    this.#pointerActive = false;
    this.#scheduleScrollSync();
  };

  #handleScrollerResize = (entries) => {
    const scroller = this.#resizeObservedScroller;
    const entry = entries.find((candidate) => candidate.target === scroller);
    if (!scroller || !entry) return;

    const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
    const inlineSize = borderBoxSize?.inlineSize ?? entry.contentRect.width;
    if (!Number.isFinite(inlineSize) || inlineSize <= 0) return;

    const previousInlineSize = this.#scrollerInlineSize;
    this.#scrollerInlineSize = inlineSize;
    if (previousInlineSize === undefined || Math.abs(previousInlineSize - inlineSize) < 0.5 || !this.#isMobile()) return;

    if (this.#pointerActive) {
      this.#resizeAlignmentPending = true;
      return;
    }

    this.#scheduleResizeAlignment();
  };

  #handleFocusIn = () => {
    if (this.#isMobile()) this.#pauseForInteraction();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopAutoplay();
      return;
    }

    this.#startAutoplayIfAllowed();
  };

  #handleBreakpointChange = () => {
    this.#syncMode({ scroll: true });

    if (this.#isMobile()) {
      this.#startAutoplayIfAllowed();
    } else {
      this.#stopAutoplay();
    }
  };

  #handleReducedMotionChange = (event) => {
    if (event.matches) {
      this.#stopAutoplay();
      return;
    }

    this.#startAutoplayIfAllowed();
  };

  #handleBlockSelect = (event) => {
    const slides = this.#getSlides();
    if (slides.length === 0) return;

    const target = event.target instanceof Element ? event.target : null;
    const targetBlock = target?.closest('[data-block-id], [data-hero-gallery-slide-id]');
    const detail = /** @type {{ blockId?: string, block?: { id?: string } } | undefined} */ (event.detail);
    const blockId =
      targetBlock?.getAttribute('data-block-id') ||
      targetBlock?.getAttribute('data-hero-gallery-slide-id') ||
      detail?.blockId ||
      detail?.block?.id;

    let index = target ? slides.findIndex((slide) => slide === target || slide.contains(target)) : -1;
    if (index === -1 && blockId) index = slides.findIndex((slide) => this.#getBlockId(slide) === blockId);
    if (index === -1) return;

    this.#stopAutoplay();
    this.select(index, undefined, {
      animate: false,
      dispatch: true,
      scroll: this.#isMobile(),
      trigger: 'editor',
    });
  };

  #scheduleScrollSync() {
    if (this.#scrollTimer) window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      this.#scrollTimer = undefined;
      this.#finishScrollSync();
    }, SCROLL_DEBOUNCE_MS);
  }

  #finishScrollSync() {
    const normalized = this.#syncCurrentFromScroll({ normalizeSentinel: !this.#pointerActive });
    if (!normalized) this.#releaseProgrammaticScroll();
    this.#flushPendingResizeAlignment(normalized);
  }

  #flushPendingResizeAlignment(alreadyAligned) {
    if (!this.#resizeAlignmentPending || this.#pointerActive) return;

    this.#resizeAlignmentPending = false;
    if (!this.isConnected || !this.#isMobile() || alreadyAligned) return;

    this.#scheduleResizeAlignment();
  }

  #scheduleResizeAlignment() {
    if (this.#resizeFrame) window.cancelAnimationFrame(this.#resizeFrame);

    this.#resizeFrame = window.requestAnimationFrame(() => {
      this.#resizeFrame = undefined;
      if (!this.isConnected || !this.#isMobile()) return;

      if (this.#pointerActive) {
        this.#resizeAlignmentPending = true;
        return;
      }

      const slides = this.#getSlides();
      const activeSlide = slides[this.#clampIndex(this.#currentIndex, slides.length)];
      if (!activeSlide) return;

      this.#syncLoopSentinels(slides);
      this.#scrollToSlide(activeSlide, false, { allowLoopPath: false });
    });
  }

  #syncCurrentFromScroll({ normalizeSentinel = true } = {}) {
    const scroller = this.#getScroller();
    const slides = this.#getSlides();
    if (!scroller || slides.length === 0 || !this.#isMobile()) return false;

    const scrollerRect = scroller.getBoundingClientRect();
    const center = scrollerRect.left + scrollerRect.width / 2;
    const sentinels = [this.#leadingSentinel, this.#trailingSentinel].filter(
      (item) => item instanceof HTMLElement && item.parentElement === scroller
    );
    const candidates = [...slides, ...sentinels];
    let closestItem = candidates[0];
    let closestDistance = Number.POSITIVE_INFINITY;

    candidates.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    const sentinelIndex = Number.parseInt(closestItem?.dataset.heroGalleryRealIndex ?? '', 10);
    const isSentinel = closestItem?.hasAttribute('data-hero-gallery-clone') && Number.isFinite(sentinelIndex);
    const closestIndex = isSentinel ? this.#clampIndex(sentinelIndex, slides.length) : slides.indexOf(closestItem);
    if (closestIndex === -1) return false;

    if (closestIndex !== this.#currentIndex) {
      this.#applyState(closestIndex, {
        animate: false,
        dispatch: true,
        scroll: false,
        trigger: 'scroll',
        userInitiated: !this.#programmaticScroll,
      });
    }

    if (
      isSentinel &&
      normalizeSentinel &&
      !this.#pointerActive &&
      closestItem instanceof HTMLElement &&
      closestDistance <= Math.max(2, closestItem.getBoundingClientRect().width * 0.1)
    ) {
      this.#settleLoopSentinel(closestIndex);
      return true;
    }

    return false;
  }

  #syncMode({ scroll }) {
    const mobile = this.#isMobile();
    this.dataset.mode = mobile ? 'mobile' : 'desktop';
    this.classList.toggle('is-mobile', mobile);
    this.classList.toggle('is-desktop', !mobile);

    if (mobile) {
      this.#syncLoopSentinels();
    } else {
      this.#pointerActive = false;
      this.#resizeAlignmentPending = false;
      if (this.#resizeFrame) window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = undefined;
      this.#removeLoopSentinels();
    }

    this.#applyState(this.#currentIndex, {
      animate: false,
      dispatch: false,
      scroll: mobile && scroll,
      trigger: 'select',
    });
  }

  #applyState(index, options = {}) {
    const slides = this.#getSlides();
    if (slides.length === 0) return;

    const previousIndex = this.#currentIndex;
    const nextIndex = this.#clampIndex(index, slides.length);
    const changed = nextIndex !== previousIndex;
    const activeSlide = slides[nextIndex];
    if (!activeSlide) return;

    this.#currentIndex = nextIndex;
    this.dataset.activeIndex = `${nextIndex}`;

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === nextIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('data-active', `${active}`);
      slide.setAttribute('aria-hidden', `${!active}`);

      if ('inert' in slide) {
        slide.inert = !active;
      } else {
        slide.toggleAttribute('inert', !active);
      }
    });

    this.#updateTabs(nextIndex);
    this.#updateCounter(nextIndex, slides.length);
    this.#updateControlState(nextIndex, slides.length);

    if (changed && options.dispatch !== false) {
      this.#updateLiveRegion(nextIndex, slides.length, activeSlide, options.trigger || 'select');
    }

    if (options.focus) this.#getTabs()[nextIndex]?.focus({ preventScroll: true });

    if (this.#isMobile() && options.scroll !== false) {
      this.#scrollToSlide(activeSlide, options.animate !== false, {
        previousIndex,
        nextIndex,
      });
    }

    if (changed && options.dispatch !== false) {
      this.#dispatchSelection({
        event: options.event,
        index: nextIndex,
        previousIndex,
        slide: activeSlide,
        trigger: options.trigger || 'select',
        userInitiated: options.userInitiated,
      });
    }
  }

  #updateTabs(activeIndex) {
    const tabs = this.#getTabs();
    const slides = this.#getSlides();

    tabs.forEach((tab, index) => {
      const active = index === activeIndex;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', `${active}`);
      tab.setAttribute('tabindex', active ? '0' : '-1');

      const slide = slides[index];
      if (!slide) return;

      if (!slide.id) slide.id = this.#getSlideDomId(slide, index);
      tab.setAttribute('aria-controls', slide.id);
    });
  }

  #updateCounter(index, count) {
    const current = this.refs.current;
    const total = this.refs.total;
    if (current instanceof HTMLElement) current.textContent = `${index + 1}`;
    if (total instanceof HTMLElement) total.textContent = `${count}`;
  }

  #updateLiveRegion(index, count, slide, trigger) {
    const live = this.refs.live;
    if (!(live instanceof HTMLElement)) return;

    const shouldAnnounce = ['click', 'keyboard', 'arrow', 'scroll', 'editor'].includes(trigger);
    live.setAttribute('aria-live', shouldAnnounce ? 'polite' : 'off');

    if (!shouldAnnounce) {
      live.textContent = '';
      return;
    }

    const heading = slide.querySelector('.ulif-hero-gallery__heading')?.textContent?.trim();
    live.textContent = heading ? `Slide ${index + 1} of ${count}: ${heading}` : `Slide ${index + 1} of ${count}`;
  }

  #updateControlState(index, count) {
    const previous = this.refs.previous;
    const next = this.refs.next;
    const loop = this.#loopEnabled();

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = count <= 1 || (!loop && index === 0);
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = count <= 1 || (!loop && index === count - 1);
    }
  }

  #scrollToSlide(slide, animate, options = {}) {
    const scroller = this.#getScroller();
    if (!scroller) return;

    const slides = this.#getSlides();
    const allowLoopPath = options.allowLoopPath !== false;
    let scrollTarget = slide;

    if (allowLoopPath && this.#loopEnabled() && slides.length > 1) {
      const lastIndex = slides.length - 1;
      if (options.previousIndex === lastIndex && options.nextIndex === 0 && this.#trailingSentinel) {
        scrollTarget = this.#trailingSentinel;
      } else if (options.previousIndex === 0 && options.nextIndex === lastIndex && this.#leadingSentinel) {
        scrollTarget = this.#leadingSentinel;
      }
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = scrollTarget.getBoundingClientRect();
    const left = scroller.scrollLeft + slideRect.left - scrollerRect.left;
    const behavior = animate && !prefersReducedMotion() ? 'smooth' : 'auto';

    if (Math.abs(scroller.scrollLeft - left) <= 1) {
      this.#releaseProgrammaticScroll();
      return;
    }

    this.#programmaticScroll = true;
    this.#setScrollPosition(scroller, left, behavior);

    if (this.#programmaticScrollTimer) window.clearTimeout(this.#programmaticScrollTimer);
    this.#programmaticScrollTimer = window.setTimeout(
      this.#releaseProgrammaticScroll,
      behavior === 'smooth' ? 600 : SCROLL_DEBOUNCE_MS
    );
  }

  #setScrollPosition(scroller, left, behavior) {
    if (behavior === 'smooth') {
      scroller.scrollTo({ left, behavior });
      return;
    }

    const previousScrollBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = left;
    scroller.style.scrollBehavior = previousScrollBehavior;
  }

  #settleLoopSentinel(index) {
    const scroller = this.#getScroller();
    const slide = this.#getSlides()[index];
    if (!scroller || !slide) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const left = scroller.scrollLeft + slideRect.left - scrollerRect.left;

    this.#programmaticScroll = true;
    this.#setScrollPosition(scroller, left, 'auto');

    if (this.#programmaticScrollTimer) window.clearTimeout(this.#programmaticScrollTimer);
    this.#programmaticScrollTimer = window.setTimeout(this.#releaseProgrammaticScroll, SCROLL_DEBOUNCE_MS);
  }

  #releaseProgrammaticScroll = () => {
    this.#programmaticScroll = false;
    if (this.#programmaticScrollTimer) window.clearTimeout(this.#programmaticScrollTimer);
    this.#programmaticScrollTimer = undefined;
  };

  #dispatchSelection({ event, index, previousIndex, slide, trigger, userInitiated }) {
    const blockId = this.#getBlockId(slide);
    const initiated = userInitiated ?? Boolean(event && trigger !== 'autoplay' && trigger !== 'editor');
    const slideshowTrigger = trigger === 'scroll' ? 'scroll' : 'select';

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: initiated,
        trigger: slideshowTrigger,
        slide,
        id: blockId || null,
      })
    );

    this.dispatchEvent(
      new CustomEvent('ulif:hero-gallery:change', {
        bubbles: true,
        detail: {
          index,
          previousIndex,
          blockId: blockId || null,
          trigger,
        },
      })
    );
  }

  #startAutoplayIfAllowed() {
    this.#stopAutoplay();

    if (
      !this.#autoplayEnabled() ||
      this.#autoplayBlocked ||
      !this.#isMobile() ||
      this.#isDesignMode() ||
      prefersReducedMotion() ||
      document.hidden ||
      this.#getSlides().length <= 1
    ) {
      return;
    }

    this.dataset.autoplayState = 'playing';
    this.toggleAttribute('paused', false);
    this.#autoplayTimer = window.setInterval(() => this.next(), this.#autoplayInterval());
  }

  #stopAutoplay() {
    if (this.#autoplayTimer) window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
    this.dataset.autoplayState = 'paused';
    this.toggleAttribute('paused', true);
  }

  #pauseForInteraction() {
    if (!this.#autoplayEnabled()) return;
    this.#autoplayBlocked = true;
    this.#stopAutoplay();
  }

  #autoplayEnabled() {
    const value = this.dataset.mobileAutoplay ?? this.getAttribute('mobile-autoplay');
    return value === '' || value === 'true' || value === '1' || value === 'on';
  }

  #autoplayInterval() {
    const raw = this.dataset.mobileAutoplaySpeed ?? this.getAttribute('mobile-autoplay-speed');
    const seconds = Number.parseFloat(raw || `${DEFAULT_AUTOPLAY_SECONDS}`);
    const safeSeconds = Number.isFinite(seconds) ? Math.max(1, seconds) : DEFAULT_AUTOPLAY_SECONDS;
    return safeSeconds * 1000;
  }

  #getInitialIndex(count) {
    const zeroBased = Number.parseInt(this.dataset.initialIndex ?? this.dataset.activeIndex ?? '', 10);
    if (Number.isFinite(zeroBased)) return this.#clampIndex(zeroBased, count);

    const oneBased = Number.parseInt(this.dataset.defaultActiveSlide ?? '', 10);
    return this.#clampIndex(Number.isFinite(oneBased) ? oneBased - 1 : 0, count);
  }

  #normalizeSelectArguments(input, event, options) {
    if (this.#isEvent(input)) {
      return {
        index: this.#currentIndex,
        event: input,
        options: this.#isPlainObject(event) ? event : options,
      };
    }

    if (this.#isPlainObject(event) && !this.#isEvent(event)) {
      return { index: Number(input), event: undefined, options: event };
    }

    return {
      index: Number(input),
      event: this.#isEvent(event) ? event : undefined,
      options: options || {},
    };
  }

  #triggerFromEvent(event) {
    switch (event?.type) {
      case 'pointerenter':
        return 'hover';
      case 'focus':
      case 'focusin':
        return 'focus';
      case 'keydown':
        return 'keyboard';
      case 'click':
        return 'click';
      default:
        return 'select';
    }
  }

  #indexForTab(value) {
    if (!(value instanceof HTMLButtonElement)) return -1;

    const explicit = Number.parseInt(value.dataset.slideIndex ?? value.dataset.heroGallerySlideIndex ?? '', 10);
    if (Number.isFinite(explicit)) return explicit;

    return this.#getTabs().indexOf(value);
  }

  #syncLoopSentinels(slides = this.#getSlides()) {
    const scroller = this.#getScroller();
    if (!scroller || !this.#isMobile() || !this.#loopEnabled() || slides.length <= 1) {
      return this.#removeLoopSentinels();
    }

    const existing = Array.from(scroller.children).filter(
      (child) => child instanceof HTMLElement && child.hasAttribute('data-hero-gallery-clone')
    );
    let leading = existing.find((clone) => clone.dataset.heroGalleryClone === 'leading');
    let trailing = existing.find((clone) => clone.dataset.heroGalleryClone === 'trailing');
    let changed = false;

    for (const clone of existing) {
      if (clone !== leading && clone !== trailing) {
        clone.remove();
        changed = true;
      }
    }

    const desiredLeading = this.#createLoopSentinel(slides[slides.length - 1], 'leading', slides.length - 1);
    const desiredTrailing = this.#createLoopSentinel(slides[0], 'trailing', 0);

    if (!(leading instanceof HTMLElement) || !leading.isEqualNode(desiredLeading)) {
      leading?.replaceWith(desiredLeading);
      leading = desiredLeading;
      changed = true;
    }
    if (!(trailing instanceof HTMLElement) || !trailing.isEqualNode(desiredTrailing)) {
      trailing?.replaceWith(desiredTrailing);
      trailing = desiredTrailing;
      changed = true;
    }

    if (scroller.firstElementChild !== leading) {
      scroller.prepend(leading);
      changed = true;
    }
    if (scroller.lastElementChild !== trailing) {
      scroller.append(trailing);
      changed = true;
    }

    this.#leadingSentinel = leading;
    this.#trailingSentinel = trailing;
    return changed;
  }

  #createLoopSentinel(source, position, realIndex) {
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    const elements = [clone, ...clone.querySelectorAll('*')];

    for (const element of elements) {
      for (const attribute of Array.from(element.attributes)) {
        const name = attribute.name.toLowerCase();
        if (
          name === 'id' ||
          name === 'ref' ||
          name === 'aria-labelledby' ||
          name === 'aria-controls' ||
          name === 'data-block-id' ||
          name === 'data-hero-gallery-slide' ||
          name === 'data-hero-gallery-slide-id' ||
          name === 'data-hero-gallery-slide-index' ||
          name === 'data-hero-gallery-action' ||
          name.startsWith('data-shopify-editor-') ||
          name.startsWith('on:') ||
          /^on[a-z]/.test(name)
        ) {
          element.removeAttribute(attribute.name);
        }
      }
    }

    clone.classList.remove('is-active');
    clone.removeAttribute('data-active');
    clone.id = this.#getSentinelDomId(position);
    clone.dataset.heroGalleryClone = position;
    clone.dataset.heroGalleryRealIndex = `${realIndex}`;
    clone.dataset.heroGalleryMorphKey = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('role', 'presentation');
    clone.setAttribute('inert', '');
    clone.inert = true;
    clone.style.pointerEvents = 'none';

    const focusableSelector =
      'a[href], button, input, select, textarea, iframe, summary, audio[controls], video[controls], [tabindex], [contenteditable="true"]';
    for (const focusable of clone.querySelectorAll(focusableSelector)) {
      focusable.setAttribute('tabindex', '-1');
      if (focusable.hasAttribute('contenteditable')) focusable.setAttribute('contenteditable', 'false');
    }

    return clone;
  }

  #getSentinelDomId(position) {
    const galleryId =
      this.id || this.dataset.sectionId || this.dataset.heroGallerySectionId || 'ulif-hero-gallery-instance';
    return `${galleryId}-sentinel-${position}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  #removeLoopSentinels() {
    const scroller = this.#getScroller() || this.#boundScroller;
    let changed = false;

    if (scroller) {
      for (const clone of scroller.querySelectorAll(':scope > [data-hero-gallery-clone]')) {
        clone.remove();
        changed = true;
      }
    }

    this.#leadingSentinel = undefined;
    this.#trailingSentinel = undefined;
    return changed;
  }

  #getSlides() {
    const refs = this.refs.slides;
    const slides = Array.isArray(refs) ? refs : [];
    return slides.filter((slide) => slide instanceof HTMLElement);
  }

  #getTabs() {
    const refs = this.refs.tabs;
    const tabs = Array.isArray(refs) ? refs : [];
    return tabs.filter((tab) => tab instanceof HTMLButtonElement);
  }

  #getScroller() {
    return this.refs.scroller instanceof HTMLElement ? this.refs.scroller : undefined;
  }

  #getBlockId(slide) {
    return slide.dataset.blockId || slide.dataset.heroGallerySlideId || slide.getAttribute('block-id') || '';
  }

  #getSlideDomId(slide, index) {
    const sectionId = this.dataset.sectionId || this.dataset.heroGallerySectionId || 'ulif-hero-gallery';
    const blockId = this.#getBlockId(slide) || `${index + 1}`;
    return `${sectionId}-slide-${blockId}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  #clampIndex(value, count) {
    const index = Number.isFinite(Number(value)) ? Math.trunc(Number(value)) : 0;
    return Math.min(Math.max(index, 0), Math.max(count - 1, 0));
  }

  #loopEnabled() {
    return this.dataset.loop !== 'false';
  }

  #isMobile() {
    return !mediaQueryLarge.matches;
  }

  #canHover() {
    return typeof window.matchMedia !== 'function' || window.matchMedia(HOVER_QUERY).matches;
  }

  #isDesignMode() {
    return Boolean(
      window.Shopify?.designMode ||
        this.dataset.designMode === 'true' ||
        document.documentElement.classList.contains('shopify-design-mode')
    );
  }

  #isEvent(value) {
    return typeof Event !== 'undefined' && value instanceof Event;
  }

  #isPlainObject(value) {
    return value !== null && typeof value === 'object' && !this.#isEvent(value);
  }

  #clearScrollTimers() {
    if (this.#scrollTimer) window.clearTimeout(this.#scrollTimer);
    if (this.#programmaticScrollTimer) window.clearTimeout(this.#programmaticScrollTimer);
    this.#scrollTimer = undefined;
    this.#programmaticScrollTimer = undefined;
    this.#programmaticScroll = false;
    this.#pointerActive = false;
  }
}

if (!customElements.get('ulif-hero-gallery')) {
  customElements.define('ulif-hero-gallery', UlifHeroGallery);
}
